/**
 * PROTOTYPE — floating variant bar. Hidden in production.
 * Not part of any product UI.
 */
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

export type PrototypeVariant = { key: string; name: string };

export function PrototypeSwitcher({
  variants,
  param = 'variant',
}: {
  variants: PrototypeVariant[];
  param?: string;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const keys = variants.map((item) => item.key);
  const currentKey = searchParams.get(param) ?? keys[0];
  const index = Math.max(0, keys.indexOf(currentKey));
  const current = variants[index] ?? variants[0];

  const go = (nextIndex: number) => {
    const wrapped = (nextIndex + variants.length) % variants.length;
    const next = new URLSearchParams(searchParams);
    next.set(param, variants[wrapped].key);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        go(index - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        go(index + 1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, searchParams]);

  if (process.env.NODE_ENV === 'production') {
    return null;
  }

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[80] flex justify-center px-4">
      <div className="pointer-events-auto flex items-center gap-2 rounded-full border border-border bg-foreground px-2 py-1 text-background shadow-lg">
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-10 w-10 text-background hover:bg-background/20 hover:text-background"
          aria-label="Previous prototype variant"
          onClick={() => go(index - 1)}
        >
          <ChevronLeft />
        </Button>
        <p className="min-w-[12rem] text-center text-sm font-semibold">
          {current.key} — {current.name}
        </p>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-10 w-10 text-background hover:bg-background/20 hover:text-background"
          aria-label="Next prototype variant"
          onClick={() => go(index + 1)}
        >
          <ChevronRight />
        </Button>
      </div>
    </div>
  );
}
